import axios from "axios";
import { toast } from "react-toastify";

// Create a logout callback that will be set by the Auth Context
let logoutCallback = null;

// Function to set the logout callback
export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

// Create an Axios instance with default config
const api = axios.create({
  baseURL: "https://api.houfalmanazel.com/api/v1",
  // baseURL: "https://dev.houfalmanazel.com/api/v1",

    // baseURL: "http://localhost:8000/api/v1",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor for adding auth token and language
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Get language from localStorage (default to Arabic if not set)
    const language = localStorage.getItem("language") || "ar";
    config.headers["Accept-Language"] = language;

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    // Check for "fail" status in response data (even in successful HTTP responses)
    if (response.data && response.data.status === "fail") {
      // Show toast notification with error message
      const errorMessage = response.data.message || "Operation failed";
      toast.error(errorMessage);

      const error = new Error(errorMessage);
      error.response = response;
      error.isApiError = true;
      error.data = response.data;
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    // Handle errors with response
    if (error.response) {
      const { response } = error;
      const status = response.status;

      // Extract error message from response data if available
      let errorMessage = "An error occurred";
      if (response.data) {
        if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (response.data.message) {
          errorMessage = response.data.message;
        } else if (response.data.error) {
          errorMessage = response.data.error;
        } else if (response.data.details) {
          errorMessage = response.data.details;
        }
      }

      // Attach the error message to the error object
      error.message = errorMessage;

      // Handle specific status codes
      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          console.log("401 Unauthorized - triggering logout");

          // Don't show toast here as logout will show its own message
          // Clear token immediately
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("singleBooking");

          // Trigger logout callback if available
          if (logoutCallback && typeof logoutCallback === "function") {
            try {
              logoutCallback(true); // Pass true to indicate it's an automatic logout
            } catch (callbackError) {
              console.error("Error in logout callback:", callbackError);
            }
          }
          break;

        case 403:
          // Forbidden - user doesn't have permission
          toast.error("You don't have permission to perform this action");
          break;

        case 404:
          // Not found
          // toast.error("Requested resource not found");
          break;

        case 422:
          // Validation error
          if (response.data && response.data.errors) {
            // Handle validation errors (usually an object with field errors)
            const validationErrors = response.data.errors;
            if (typeof validationErrors === "object") {
              const firstError = Object.values(validationErrors)[0];
              toast.error(
                Array.isArray(firstError) ? firstError[0] : firstError
              );
            } else {
              toast.error(errorMessage);
            }
          } else {
            toast.error(errorMessage);
          }
          break;

        case 429:
          // Too many requests
          toast.error("Too many requests. Please try again later");
          break;

        case 500:
          // Internal server error
          toast.error("Server error. Please try again later");
          break;

        case 502:
        case 503:
        case 504:
          // Server errors
          toast.error(
            "Service temporarily unavailable. Please try again later"
          );
          break;

        default:
          // For other status codes, show the error message
          toast.error(errorMessage);
      }
    } else if (error.request) {
      // Request was made but no response received
      const errorMessage = "Network error. Please check your connection";
      error.message = errorMessage;
      toast.error(errorMessage);
    } else if (error.code === "ECONNABORTED") {
      // Request timeout
      const errorMessage = "Request timeout. Please try again";
      error.message = errorMessage;
      toast.error(errorMessage);
    } else {
      // Something happened in setting up the request
      const errorMessage = error.message || "An unexpected error occurred";
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

export default api;
